import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { CrowdTask, CrowdAssignment } from '../types'

interface Props {
  task: CrowdTask
  assignment?: CrowdAssignment
  isNew?: boolean
  submittedCount?: number
}

function TaskTypeIcon({ type }: { type: string }) {
  if (type === 'spot') {
    return (
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0"
        style={{ background: '#fef3c7' }}
      >
        ⚡
      </div>
    )
  }
  return (
    <div
      className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
      style={{ background: '#e0e7ff' }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="8" height="8" rx="2" fill="#6366f1" />
        <rect x="13" y="3" width="8" height="8" rx="2" fill="#6366f1" />
        <rect x="3" y="13" width="8" height="8" rx="2" fill="#6366f1" />
        <rect x="13" y="13" width="8" height="8" rx="2" fill="#6366f1" />
      </svg>
    </div>
  )
}

export default function TaskCard({ task, assignment, isNew, submittedCount }: Props) {
  const { t } = useTranslation('worker')
  const navigate = useNavigate()

  const ASSIGNMENT_STATUS_BADGE: Record<string, { label: string; bg: string; color: string }> = {
    submitted: { label: t('taskCard.statusReview'), bg: '#f59e0b22', color: '#f59e0b' },
    completed: { label: t('taskCard.statusDone'), bg: '#22c55e22', color: '#22c55e' },
    abandoned: { label: t('taskCard.statusAbandoned'), bg: '#6b728022', color: '#6b7280' },
  }
  const isDone = assignment?.status === 'completed' || assignment?.status === 'abandoned'
  const isSpot = task.task_type === 'spot'
  const estimatedMinutes = Math.max(5, Math.round(task.items.length * 1.5))
  const totalReward =
    task.reward_per_item_coins != null ? task.reward_per_item_coins * task.items.length : null
  const totalItems = task.items.length
  const foundItems = submittedCount ?? (assignment ? assignment.items_found + assignment.items_not_found : 0)

  return (
    <div
      className="rounded-2xl overflow-hidden cursor-pointer active:opacity-75 transition-opacity flex items-center gap-3 p-4"
      style={{
        background: 'var(--app-card-bg)',
        opacity: isDone ? 0.55 : 1,
        boxShadow: isSpot && !assignment
          ? '0 0 0 1.5px rgba(245,158,11,0.35), 0 1px 4px rgba(0,0,0,0.06)'
          : '0 1px 4px rgba(0,0,0,0.06)',
        borderLeft: isSpot && !assignment ? '3px solid #f59e0b' : '3px solid transparent',
      }}
      onClick={() => navigate(`/tasks/${task.id}`)}
    >
      <TaskTypeIcon type={task.task_type} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
          <h3
            className="font-bold text-base leading-tight"
            style={{ color: 'var(--tg-theme-text-color)' }}
          >
            {task.title}
          </h3>
          <span
            className="text-xs px-1.5 py-0.5 rounded-md font-semibold flex-shrink-0"
            style={{
              background: isSpot ? '#fef3c7' : '#e0e7ff',
              color: isSpot ? '#d97706' : '#4f46e5',
            }}
          >
            {task.task_type.toUpperCase()}
          </span>
          {isNew && (
            <span
              className="text-xs px-1.5 py-0.5 rounded-md font-bold flex-shrink-0"
              style={{ background: '#dcfce7', color: '#16a34a' }}
            >
              NEW
            </span>
          )}
          {assignment && ASSIGNMENT_STATUS_BADGE[assignment.status] && (
            <span
              className="text-xs px-1.5 py-0.5 rounded-md font-medium flex-shrink-0"
              style={{
                background: ASSIGNMENT_STATUS_BADGE[assignment.status].bg,
                color: ASSIGNMENT_STATUS_BADGE[assignment.status].color,
              }}
            >
              {ASSIGNMENT_STATUS_BADGE[assignment.status].label}
            </span>
          )}
        </div>

        <div
          className="flex items-center gap-1 text-xs flex-wrap"
          style={{ color: 'var(--tg-theme-text-color)', opacity: 0.5 }}
        >
          <span>{t('taskCard.items', { count: task.items.length })}</span>
          <span>·</span>
          <span>{t('taskCard.minEst', { min: estimatedMinutes })}</span>
          {task.target_location_name && (
            <>
              <span>·</span>
              <span>{task.target_location_name}</span>
            </>
          )}
        </div>

        {/* Progress bar for active assignments */}
        {assignment && assignment.status !== 'completed' && totalItems > 0 && (
          <div
            className="mt-2 h-1 rounded-full overflow-hidden"
            style={{ background: 'var(--app-page-bg)' }}
          >
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${totalItems > 0 ? (foundItems / totalItems) * 100 : 0}%`,
                background: 'var(--tg-theme-button-color)',
              }}
            />
          </div>
        )}
      </div>

      {/* Reward badge */}
      {task.reward_per_item_coins != null && task.reward_per_item_coins > 0 && (
        <div className="flex flex-col items-center flex-shrink-0 gap-0.5">
          <div
            className="px-2.5 py-1 rounded-xl font-bold text-sm flex items-center gap-1"
            style={{ background: 'rgba(251,191,36,0.15)', color: '#f59e0b' }}
          >
            🪙 {totalReward != null ? totalReward.toLocaleString() : task.reward_per_item_coins}
          </div>
          <p className="text-[10px] font-medium text-center" style={{ color: '#f59e0b', opacity: 0.7 }}>
            {totalItems > 1
              ? t('taskCard.perItem', { coins: task.reward_per_item_coins })
              : t('taskCard.coinsReward')}
          </p>
        </div>
      )}
    </div>
  )
}
